import type { ReactNode } from "react";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";

/**
 * Break-glass: the frame both location wizards pin their pages to.
 *
 * Three things no layout primitive can express:
 * - the page width, which is load-bearing — it stops the address grid
 *   shrink-wrapping and keeps the dialog from resizing between pages. It is
 *   the `wide` dialog (896px) less its 32px body padding either side, so the
 *   two column pairs on the details page each get ~384px. At the old 672 they
 *   got 296, which wrapped "Delivery appointment required" onto two lines and
 *   clipped "Warehouse in Industrial Area" under the dropdown caret;
 * - a floor on the height, so the modify wizard doesn't shrink and grow as you
 *   step through it (Stack/Row only distribute space they are given);
 * - the `data-testid` the address-book Cypress smoke test selects on, which
 *   Stack and Row don't take.
 *
 * It also declares the underline presentation. Dashboard is a customer app and
 * Cargo drew these wizards with underlined fields; a dialog inherits whatever
 * the screen behind it declared, and the address book declares nothing, so
 * without this the fields come out in the dense boxed admin style.
 */
export function LocationsWizardBody({
  testId,
  heightFloor,
  children,
}: {
  testId: string;
  /** Only the modify wizard needs it — its pages differ most in height. */
  heightFloor?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <ControlPresentationProvider presentation="underlined">
      <div
        className={
          heightFloor
            ? "min-h-[330px] w-[832px] max-w-full"
            : "w-[832px] max-w-full"
        }
        data-testid={testId}
      >
        {children}
      </div>
    </ControlPresentationProvider>
  );
}
